import { useState } from "react";
import { logoUrl } from "@/lib/logoDev";
import { cn } from "@/lib/utils";

interface ToolLogoProps {
  name: string;
  domain: string;
  /** Rendered pixel size of the logo image (retina handled by Logo.dev). */
  size?: number;
  className?: string;
}

/**
 * A single tool logo, fetched from Logo.dev. Renders greyscale-by-default and
 * pops to full colour on hover (the premium "logo wall" pattern). Falls back to
 * a monogram tile when the logo or the API token is unavailable.
 */
const ToolLogo = ({ name, domain, size = 88, className }: ToolLogoProps) => {
  const [failed, setFailed] = useState(false);
  const src = logoUrl(domain, size);

  if (!src || failed) {
    const initial = name.trim().charAt(0).toUpperCase();
    return (
      <span
        className={cn(
          "flex items-center justify-center rounded-lg bg-background/60 font-heading text-lg font-bold text-accent",
          className
        )}
        style={{ width: size / 2, height: size / 2 }}
        aria-hidden
      >
        {initial}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={`${name} logo`}
      width={size / 2}
      height={size / 2}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={cn(
        "h-9 w-auto max-w-[120px] object-contain grayscale opacity-70 transition-all duration-300 group-hover/logo:grayscale-0 group-hover/logo:opacity-100",
        className
      )}
    />
  );
};

export default ToolLogo;
